import React from 'react';
import { Button, Form, Input, message, Modal, Spin, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import type { UploadRequestOption as RcUploadRequestOption } from 'rc-upload/lib/interface';
import StateSelect from 'components/form/StateSelect';
import USFaxNumberInput from 'components/form/USFaxNumberInput';
import { faxNumberPortInSubmit, faxNumberPortInUpload } from 'api/fax';
import FormValidationHelper from 'utils/validation';
import { isFaxScheduleSuccess, normalizeFaxForApi } from 'utils/eFaxScheduleRows';
import s from './s.module.less';
import ps from '../../s.module.less';

type Props = {
    transferNumber: string;
    onBack: () => void;
    onSubmit: () => void;
};

type TransferFormValues = {
    endUserName: string;
    authorizedPerson: string;
    efaxNumber: string;
    carrierAccount: string;
    pinPasscode?: string;
    endUserState?: string;
    endUserCity?: string;
    endUserPostalCode?: string;
    practiceAddress?: string;
};

const TransferForm = ({ transferNumber, onBack, onSubmit }: Props) => {
    const [form] = Form.useForm<TransferFormValues>();
    const [loading, setLoading] = React.useState(false);
    const [recentBillList, setRecentBillList] = React.useState<UploadFile[]>([]);
    const [loaFileList, setLoaFileList] = React.useState<UploadFile[]>([]);
    const [recentBillUploadId, setRecentBillUploadId] = React.useState<number | null>(null);
    const [loaUploadId, setLoaUploadId] = React.useState<number | null>(null);
    const [recentBillPreviewUrl, setRecentBillPreviewUrl] = React.useState<string | null>(null);
    const [loaPreviewUrl, setLoaPreviewUrl] = React.useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewTitle, setPreviewTitle] = React.useState<string>('');
    const [previewUrl, setPreviewUrl] = React.useState<string>('');
    const [previewIsPdf, setPreviewIsPdf] = React.useState(false);

    // Enable submit once user selected both files.
    // Upload ids may be missing if backend returns a different id field; submit will retry upload if needed.
    const hasUploads = recentBillList.length > 0 && loaFileList.length > 0;

    const isPdfFile = (file?: UploadFile | null) => {
        const name = file?.name || '';
        const type = (file as any)?.type || '';
        return name.toLowerCase().endsWith('.pdf') || type === 'application/pdf';
    };

    const makeObjectUrl = (file?: UploadFile | null) => {
        const raw = file?.originFileObj as File | undefined;
        if (!raw) return null;
        return URL.createObjectURL(raw);
    };

    // Maintain local preview URLs (no backend URL needed)
    React.useEffect(() => {
        const url = makeObjectUrl(recentBillList[0] || null);
        setRecentBillPreviewUrl((prev) => {
            if (prev && prev !== url) URL.revokeObjectURL(prev);
            return url;
        });
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recentBillList[0]?.uid]);

    React.useEffect(() => {
        const url = makeObjectUrl(loaFileList[0] || null);
        setLoaPreviewUrl((prev) => {
            if (prev && prev !== url) URL.revokeObjectURL(prev);
            return url;
        });
        return () => {
            if (url) URL.revokeObjectURL(url);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaFileList[0]?.uid]);

    const extractUploadId = (payload: any): number | null => {
        const data = payload?.data?.data ?? payload?.data ?? payload;
        const raw = data?.fileUploadId ?? data?.id ?? data?.fileId ?? data?.uploadId ?? data?.fileUploadID;
        const n = typeof raw === 'string' ? Number(raw) : raw;
        return Number.isFinite(n) ? (n as number) : null;
    };

    const ensureUploadId = async (file: File, label: 'Recent bill' | 'LOA') => {
        const up = await faxNumberPortInUpload(file);
        if (up?.error) throw new Error(typeof up.error === 'string' ? up.error : 'Upload failed');
        const id = extractUploadId(up);
        if (!id) throw new Error(`${label} upload succeeded but fileUploadId was not returned`);
        return id;
    };

    const handleSubmit = async (values: TransferFormValues) => {
        const recentBillFile = recentBillList[0]?.originFileObj as File | undefined;
        const loaFile = loaFileList[0]?.originFileObj as File | undefined;

        if (!recentBillFile) {
            message.warning('Please upload a recent bill');
            return;
        }
        if (!loaFile) {
            message.warning('Please upload a signed LOA');
            return;
        }

        setLoading(true);
        try {
            // If we couldn't parse upload ids (backend field differs), retry upload here to ensure submit works.
            const recentId = recentBillUploadId ?? (await ensureUploadId(recentBillFile, 'Recent bill'));
            const loaId = loaUploadId ?? (await ensureUploadId(loaFile, 'LOA'));
            if (!recentBillUploadId) setRecentBillUploadId(recentId);
            if (!loaUploadId) setLoaUploadId(loaId);

            const result = await faxNumberPortInSubmit({
                faxNumber: transferNumber,
                endUserName: values.endUserName,
                authorizedPerson: values.authorizedPerson,
                billingTelephoneNumber: normalizeFaxForApi(values.efaxNumber),
                carrierAccountNumber: values.carrierAccount,
                pinPasscode: values.pinPasscode,
                endUserState: values.endUserState,
                endUserCity: values.endUserCity,
                endUserPostalCode: values.endUserPostalCode,
                practiceAddress: values.practiceAddress,
                recentBillFileUploadId: recentId,
                loaFileUploadId: loaId,
            });

            const envelope = result?.data as { code?: number; message?: string } | undefined;
            if (result?.error || (envelope && !isFaxScheduleSuccess(envelope))) {
                const errText =
                    envelope?.message ||
                    (typeof result?.error === 'string' ? result.error : null) ||
                    'Failed to submit transfer request';
                message.error(errText);
                return;
            }

            message.success('Transfer request submitted successfully!');
            onSubmit();
        } catch {
            message.error('Failed to submit transfer request.');
        } finally {
            setLoading(false);
        }
    };

    const beforeUpload = (file: File) => {
        const isLt10M = file.size / 1024 / 1024 <= 10;
        if (!isLt10M) {
            message.error('File must be smaller than 10MB!');
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const makeUploadRequest = (kind: 'recentBill' | 'loa') => async (data: RcUploadRequestOption) => {
        try {
            const file = data.file as File;
            const res = await faxNumberPortInUpload(file);
            if (res?.error) {
                throw new Error(typeof res.error === 'string' ? res.error : 'Upload failed');
            }
            const uploadId = extractUploadId(res);
            if (!uploadId) {
                throw new Error('Upload succeeded but fileUploadId was not returned');
            }

            if (kind === 'recentBill') setRecentBillUploadId(uploadId);
            else setLoaUploadId(uploadId);

            data.onSuccess?.(res.data, file);
        } catch (e: any) {
            if (kind === 'recentBill') setRecentBillUploadId(null);
            else setLoaUploadId(null);
            message.error(e?.message || 'Upload failed');
            data.onError?.(e);
        }
    };

    const openPreview = (file: UploadFile, objectUrl: string | null) => {
        const pdf = isPdfFile(file);
        setPreviewIsPdf(pdf);
        setPreviewTitle(file.name || (pdf ? 'PDF' : 'Image'));
        setPreviewUrl(objectUrl || '');
        setPreviewOpen(true);
    };

    const renderFilePreviewRow = (
        kind: 'recentBill' | 'loa',
        fileList: UploadFile[],
        objectUrl: string | null,
        onRemove: () => void,
    ) => {
        const file = fileList[0];
        if (!file) return null;
        const pdf = isPdfFile(file);

        return (
            <div className={s.filePreviewRow}>
                <div className={s.filePreviewLeft}>
                    {pdf ? (
                        <div className={s.filePreviewPdf}>PDF</div>
                    ) : (
                        <img className={s.filePreviewThumb} alt={file.name} src={objectUrl || ''} />
                    )}
                    <div className={s.filePreviewName} title={file.name}>
                        {file.name}
                    </div>
                </div>
                <div className={s.filePreviewActions}>
                    <button
                        type="button"
                        className={s.filePreviewAction}
                        onClick={() => openPreview(file, objectUrl)}
                        disabled={!objectUrl}
                    >
                        Preview
                    </button>
                    <button type="button" className={s.filePreviewActionDanger} onClick={onRemove}>
                        Remove
                    </button>
                </div>
            </div>
        );
    };

    return (
        <Spin spinning={loading}>
            <button type="button" className={ps.backLink} onClick={onBack}>
                <span className={ps.backIcon}>‹</span>
                <span>Back</span>
            </button>
            <div className={ps.header}>
                <div className={ps.headerTitle}>Transfer an existing fax number</div>
                <div className={ps.headerDesc}>
                    To avoid porting delays, please ensure the accuracy of all information submitted.
                </div>
            </div>

            <div className={s.numberBadgeSection}>
                <div className={s.numberBadgeLabel}>Number requested</div>
                <span className={s.numberBadge}>{transferNumber}</span>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                autoComplete="off"
                className={s.transferForm}
                requiredMark
            >
                <div className={s.formFieldsColumn}>
                    <div className={s.sectionTitle}>Carrier information</div>

                    <div className={s.formRow}>
                        <div className={s.formCol}>
                            <Form.Item
                                name="endUserName"
                                label="End user name (Business name)"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                        <div className={s.formCol}>
                            <Form.Item
                                name="authorizedPerson"
                                label="Authorized person's name (Billing contact)"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                    </div>

                    <Form.Item
                        name="efaxNumber"
                        label="eFax number"
                        validateFirst
                        rules={[
                            { required: true, message: 'Required' },
                            FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit US phone number'),
                        ]}
                    >
                        <USFaxNumberInput placeholder="(123) 456 - 7890" />
                    </Form.Item>

                    <div className={s.formRow}>
                        <div className={s.formCol}>
                            <Form.Item
                                name="carrierAccount"
                                label="Carrier account number"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input />
                            </Form.Item>
                        </div>
                        <div className={s.formCol}>
                            <Form.Item name="pinPasscode" label="Pin/Passcode (optional)">
                                <Input />
                            </Form.Item>
                        </div>
                    </div>

                    <Form.Item name="practiceAddress" label="Practice address">
                        <Input placeholder="Street address" />
                    </Form.Item>

                    <div className={s.formRow}>
                        <div className={s.formCol}>
                            <Form.Item name="endUserState" label="State">
                                <StateSelect placeholder="Select state" className={s.fieldSelect} />
                            </Form.Item>
                        </div>
                        <div className={s.formCol}>
                            <Form.Item name="endUserCity" label="City">
                                <Input placeholder="City" />
                            </Form.Item>
                        </div>
                        <div className={s.formCol}>
                            <Form.Item name="endUserPostalCode" label="Postal code">
                                <Input placeholder="Postal code" maxLength={10} />
                            </Form.Item>
                        </div>
                    </div>
                </div>

                <div className={s.uploadSection}>
                    <div className={s.uploadBlock}>
                        <div className={s.uploadBlockHeader}>
                            <div className={s.uploadBlockTitle}>Recent bill (within 30–60 days)</div>
                        </div>
                        <Upload.Dragger
                            className={s.dragger}
                            fileList={recentBillList}
                            beforeUpload={beforeUpload}
                            customRequest={makeUploadRequest('recentBill')}
                            onChange={({ fileList }) => setRecentBillList(fileList)}
                            onRemove={() => {
                                setRecentBillUploadId(null);
                                return true;
                            }}
                            accept="image/*,application/pdf"
                            maxCount={1}
                            showUploadList={false}
                        >
                            <div className={s.draggerIcon}>
                                <UploadOutlined />
                            </div>
                            <div className={s.draggerAction}>
                                <span className={s.draggerLink}>Upload a file</span> <span>or drag and drop</span>
                            </div>
                            <div className={s.draggerHint}>PNG, JPG, GIF up to 10MB</div>
                        </Upload.Dragger>
                        {renderFilePreviewRow('recentBill', recentBillList, recentBillPreviewUrl, () => {
                            setRecentBillList([]);
                            setRecentBillUploadId(null);
                        })}
                    </div>
                    <div className={s.uploadBlock}>
                        <div className={s.uploadBlockHeader}>
                            <div className={s.uploadBlockTitle}>Signed Letter of Authorization (LOA)</div>
                            {/* <div className={s.downloadBtn}>
                                Download template
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M15 10.8333V13.3333C15 13.7754 14.8244 14.1993 14.5118 14.5118C14.1993 14.8244 13.7754 15 13.3333 15H6.66667C6.22464 15 5.80072 14.8244 5.48816 14.5118C5.17559 14.1993 5 13.7754 5 13.3333V10.8333"
                                        stroke="#374151"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M7.5 8.33334L10 10.8333L12.5 8.33334"
                                        stroke="#374151"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M10 10.8333V5"
                                        stroke="#374151"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div> */}
                        </div>
                        <Upload.Dragger
                            className={s.dragger}
                            fileList={loaFileList}
                            beforeUpload={beforeUpload}
                            customRequest={makeUploadRequest('loa')}
                            onChange={({ fileList }) => setLoaFileList(fileList)}
                            onRemove={() => {
                                setLoaUploadId(null);
                                return true;
                            }}
                            accept="image/*,application/pdf"
                            maxCount={1}
                            showUploadList={false}
                        >
                            <div className={s.draggerIcon}>
                                <UploadOutlined />
                            </div>
                            <div className={s.draggerAction}>
                                <span className={s.draggerLink}>Upload a file</span> <span>or drag and drop</span>
                            </div>
                            <div className={s.draggerHint}>PNG, JPG, GIF up to 10MB</div>
                        </Upload.Dragger>
                        {renderFilePreviewRow('loa', loaFileList, loaPreviewUrl, () => {
                            setLoaFileList([]);
                            setLoaUploadId(null);
                        })}
                    </div>
                </div>

                <Form.Item className={s.submitItem}>
                    <Button
                        type="default"
                        htmlType="submit"
                        className={`${ps.actionBtn} ${s.submitBtn}`}
                        loading={loading}
                        disabled={!hasUploads}
                    >
                        Submit transfer request
                    </Button>
                </Form.Item>
            </Form>

            <Modal
                open={previewOpen}
                title={previewTitle}
                footer={null}
                onCancel={() => setPreviewOpen(false)}
                width={800}
                destroyOnClose
            >
                {previewIsPdf ? (
                    <iframe
                        title={previewTitle}
                        src={previewUrl}
                        style={{ width: '100%', height: 600, border: 'none' }}
                    />
                ) : (
                    <img alt={previewTitle} src={previewUrl} style={{ width: '100%', height: 'auto' }} />
                )}
            </Modal>
        </Spin>
    );
};

export default TransferForm;
